import { MESApiService, getMomentTz, spParameter } from "@dexteel/mesf-core";
import { ProcessResponse } from "../models/lmf.models";

const moment = getMomentTz();

export const getLMFProcess = async ({
  ProcessId = null,
}: {
  ProcessId: number | null;
}) => {
  const apiService = new MESApiService();

  const resp = await apiService.callV2("[LMF].[DEGetProcess]", [
    { name: "ProcessId", value: ProcessId },
  ]);

  if (resp.ok) {
    const data = resp.data as ProcessResponse;

    const process = data.tables[0].rows[0];
    data.tables[0].rows[0] = {
      ...data.tables[0].rows[0],
      Start: moment.utc(process["Start"]).toDate(),
      End: process.End ? moment.utc(process["End"]).toDate() : null,
      Arrive: process.Arrive ? moment.utc(process["Arrive"]).toDate() : null,
      Ship: process.Ship ? moment.utc(process["Ship"]).toDate() : null,
      PrevEnd: process.PrevEnd ? moment.utc(process["PrevEnd"]).toDate() : null,
      PendingShipSignalTime: process.PendingShipSignalTime
        ? moment.utc(process["PendingShipSignalTime"]).toDate()
        : null,
    };
    data.tables[1].rows = data.tables[1].rows.map((row) => {
      row.Timestamp = moment.utc(row["Timestamp"]).toDate();
      return row;
    });
    data.tables[2].rows = data.tables[2].rows.map((heatingRow) => {
      heatingRow.StartTime = moment.utc(heatingRow["StartTime"]).toDate();
      heatingRow.EndTime = moment.utc(heatingRow["EndTime"]).toDate();
      return heatingRow;
    });
    data.tables[3].rows = data.tables[3].rows.map((row) => {
      row.Timestamp = moment.utc(row["Timestamp"]).toDate();
      return row;
    });

    return {
      ok: true,
      data,
    };
  } else {
    return {
      ok: false,
      message: resp.message,
    };
  }
};

export type UpsertProcessParams = {
  ProcessId: number | null;
  Start: string;
  End: string | null;
  IsShipManual?: boolean;
};
export const upsertProcess = async ({
  ProcessId,
  Start,
  End,
  IsShipManual,
}: UpsertProcessParams) => {
  const apiService = new MESApiService();

  const parameters: spParameter[] = [
    { name: "ProcessId", value: ProcessId },
    { name: "Start", value: Start },
    { name: "End", value: End },
    { name: "IsManual", value: true },
  ];

  if (IsShipManual !== undefined) {
    parameters.push({ name: "IsShipManual", value: IsShipManual });
  }

  return await apiService.callV2("[LMF].[UpsertProcess]", parameters);
};

type UpsertProcessDataParams = {
  processId: number;
  field: string;
  value: boolean | string | number | Date | null;
};

export const upsertProcessData = async ({
  processId,
  field,
  value,
}: UpsertProcessDataParams) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [
    { name: "ProcessId", value: processId },
    { name: field, value: value },
    { name: "IsManual", value: true },
  ];

  return await apiService.callV2("[LMF].[UpsertProcessData]", parameters);
};

type UpsertProbeParams = {
  ProbeId: number | null;
  ProcessId: number;
  Timestamp: string;
  Temperature: number | null;
  Carbon: number | null;
  Oxygen: number | null;
  IsProbeManual: boolean;
};

interface ChangeHeatStatusProps {
  heatId: number;
  status: string;
  ladleCar?: number;
}

export const changeLMFHeatStatus = async ({
  heatId,
  status,
  ladleCar,
}: ChangeHeatStatusProps) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [
    { name: "HeatId", value: heatId },
    { name: "HeatStatusCode", value: status.charAt(0) },
  ];
  if (ladleCar !== undefined) {
    parameters.push({ name: "LadleCar", value: ladleCar });
  }
  const resp = await apiService.callV2(
    "[RBD].[ChangeHeatStatusCommand]",
    parameters,
  );
  if (resp.ok) {
    const data = resp.data;
    return {
      ok: true,
      data: data.tables[0].rows,
    };
  } else {
    return {
      ok: false,
      message: resp.message!,
    };
  }
};

export const getLMFHeatsForSelector = async () => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [{ name: "HoursBack", value: 24 }];

  const resp = await apiService.callV2("[LMF].[GetTappedHeats]", parameters);
  if (resp.ok) {
    const data = resp.data;
    return {
      ok: true,
      data: data.tables[0].rows,
    };
  } else {
    return {
      ok: false,
      message: resp.message,
    };
  }
};

export const upsertProbe = async ({
  ProbeId,
  ProcessId,
  Timestamp,
  Temperature,
  Carbon,
  Oxygen,
  IsProbeManual,
}: UpsertProbeParams) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [
    { name: "ProbeId", value: ProbeId },
    { name: "ProcessId", value: ProcessId },
    { name: "Timestamp", value: Timestamp },
    { name: "Temperature", value: Temperature },
    { name: "Carbon", value: Carbon },
    { name: "Oxygen", value: Oxygen },
    { name: "IsProbeManual", value: IsProbeManual },
  ];

  return await apiService.callV2("[LMF].[UpsertProbe]", parameters);
};

type UpsertHeatParams = {
  HeatId: number;
  field: string;
  value: any;
};

export const upsertHeat = async ({
  HeatId,
  field,
  value,
}: UpsertHeatParams) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [
    { name: "HeatId", value: HeatId },
    { name: field, value: value },
  ];

  return await apiService.callV2("[MS].[UpsertHeat]", parameters);
};

export type UpsertAdditionParams = {
  AdditionId: number;
  ProcessId: number;
  Timestamp: Date;
  Weight: number;
  MaterialId: number;
};

export const UpsertAddition = async ({
  AdditionId,
  ProcessId,
  Timestamp,
  Weight,
  MaterialId,
}: UpsertAdditionParams) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [
    { name: "AdditionId", value: AdditionId },
    { name: "ProcessId", value: ProcessId },
    { name: "Timestamp", value: Timestamp.toISOString() },
    { name: "Weight", value: Weight },
    { name: "MaterialId", value: MaterialId },
    { name: "IsAdditionManual", value: true },
  ];

  return await apiService.callV2("[LMF].[UpsertAddition]", parameters);
};

type UpsertHeatingParams = {
  HeatingId: number | null;
  ProcessId: number;
  StartTime: string;
  EndTime: string;
  Energy: number | null;
};
export const UpsertHeating = async ({
  HeatingId,
  ProcessId,
  StartTime,
  EndTime,
  Energy,
}: UpsertHeatingParams) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [
    { name: "HeatingId", value: HeatingId },
    { name: "ProcessId", value: ProcessId },
    { name: "Start", value: StartTime },
    { name: "End", value: EndTime },
    { name: "Energy", value: Energy },
    { name: "IsHeatingManual", value: true },
  ];
  return await apiService.callV2("[LMF].[UpsertHeating]", parameters);
};

export const readElectrodesFromPLC = async ({
  ProcessId,
}: {
  ProcessId: number;
}) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [{ name: "ProcessId", value: ProcessId }];
  return await apiService.callV2(
    "[RBD].[ReadLMFElectrodesChangesFromPLC]",
    parameters,
  );
};

export const readProbesFromPLC = async ({
  ProcessId,
}: {
  ProcessId: number;
}) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [{ name: "ProcessId", value: ProcessId }];

  return await apiService.callV2("[RBD].[ReadLMFProbesFromPLC]", parameters);
};

export const readAdditionsFromPLC = async ({
  ProcessId,
}: {
  ProcessId: number;
}) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [{ name: "ProcessId", value: ProcessId }];

  return await apiService.callV2("[RBD].[ReadLMFAdditionsFromPLC]", parameters);
};

export const readWireAdditionsFromPLC = async ({
  ProcessId,
}: {
  ProcessId: number;
}) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [{ name: "ProcessId", value: ProcessId }];

  return await apiService.callV2("[RBD].[ReadLMFWireAdditionsFromPLC]", parameters);
};

export const deleteProbeById = async ({ ProbeId }: { ProbeId: number }) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [{ name: "ProbeId", value: ProbeId }];

  return await apiService.callV2("[LMF].[DeleteProbe]", parameters);
};

export const deleteAdditionById = async ({
  AdditionId,
}: {
  AdditionId: number;
}) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [{ name: "AdditionId", value: AdditionId }];

  return await apiService.callV2("[LMF].[DeleteAddition]", parameters);
};

export const getLastestProcesses = async () => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [{ name: "ProcessCode", value: "L" }];
  return await apiService.callV2("[RBD].[GetLastestNProcesses]", parameters);
};

export const searchProcesses = async (searchTerm: string) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [
    { name: "SearchTerm", value: searchTerm },
    { name: "MaxResults", value: 100 },
    { name: "AssetId", value: "12" },
  ];

  return await apiService.callV2("[RBD].[SearchProcesses]", parameters);
};

export type UpsertPlugMetricsParams = {
  ProcessId: number;
  PlugInsideVol: number | null;
  PlugInsideTime: number | null;
  PlugOutsideVol: number | null;
  PlugOutsideTime: number | null;
};

export const upsertPlugMetrics = async ({
  ProcessId,
  PlugInsideVol,
  PlugInsideTime,
  PlugOutsideVol,
  PlugOutsideTime,
}: UpsertPlugMetricsParams) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [
    { name: "ProcessId", value: ProcessId },
    { name: "PlugInsideVol", value: PlugInsideVol },
    { name: "PlugInsideTime", value: PlugInsideTime },
    { name: "PlugOutsideVol", value: PlugOutsideVol },
    { name: "PlugOutsideTime", value: PlugOutsideTime },
  ];

  return await apiService.callV2("[LMF].[UpsertPlugMetrics]", parameters);
};

export type ReadPlugFieldFromPLCParams = {
  ProcessId: number;
  Field: string;
};

export const readPlugFieldFromPLC = async ({
  ProcessId,
  Field,
}: ReadPlugFieldFromPLCParams) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [
    { name: "ProcessId", value: ProcessId },
    { name: "Field", value: Field },
  ];

  return await apiService.callV2("[RBD].[ReadPlugsFromPLC]", parameters);
};

export const readAllPlugsFromPLC = async ({
  ProcessId,
}: {
  ProcessId: number;
}) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [{ name: "ProcessId", value: ProcessId }];

  return await apiService.callV2("[RBD].[ReadPlugsFromPLC]", parameters);
};

export const readProcessDataFromPLC = async ({
  ProcessId,
}: {
  ProcessId: number;
}) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [{ name: "ProcessId", value: ProcessId }];

  return await apiService.callV2(
    "[RBD].[ReadLMFProcessDataFromPLC]",
    parameters,
  );
};

export const readHeatingsFromPLC = async ({
  ProcessId,
}: {
  ProcessId: number;
}) => {
  const apiService = new MESApiService();
  const parameters: spParameter[] = [{ name: "ProcessId", value: ProcessId }];

  return await apiService.callV2("[RBD].[ReadLMFHeatingsFromPLC]", parameters);
};
